import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import Dashboard from "./Components/Dashboard/Dashboard";
import { Provider } from "react-redux";
import { store } from "./Reducers/store";
import MemberProfile from "./Components/Member/MemberProfile";
import EditMemberProfile from "./Components/Member/EditMemberProfile";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import { fetchCurrentUser, setUserFromToken } from "./Actions/index";
import { isAfter, toDate } from "date-fns";
import { jwtDecode } from "jwt-decode";
import UserProfile from "./Components/User/UserProfile";
import EditUserProfile from "./Components/User/EditUserProfile";
import UserList from "./Components/User/UserList";
import Login from "./Components/User/Login";
import RequireAuth from "./Components/Auth/RequireAuth";
import MemberList from "./Components/Member/MemberList";
import MemberDirectory from "./Components/Member/MemberDirectory";
import ErrorPage from "./Components/Common/ErrorPage";
import { DecodedToken } from "./Models/DecodedToken";
import InvoiceList from "./Components/Member/InvoiceList";
import MemberResources from "./Components/Member/MemberResources";
import ResetPassword from "./Components/User/ResetPassword";
import ForgotPassword from "./Components/User/ForgotPassword";
import CustomSitePage from "./Components/Member/CustomSitePage";
import MemberApplication from "./Components/MembershipApplication/MemberApplication";
import ThankYou from "./Components/MembershipApplication/ThankYou";
import NewApplicant from "./Components/MembershipApplication/NewApplicant";
import AppPublic from "./AppPublic";
import MemberTags from "./Components/Member/MemberTags";
import DownloadFiles from "./Components/Member/DownloadFiles";
import FallConvention from "./Components/Member/FallConvention";
import SpringMeeting from "./Components/Member/SpringMeeting";
import IndustryShowcase from "./Components/Member/IndustryShowcase";
import IndustryShowcasePage from "./Components/Member/IndustryShowcasePage";
import config from "devextreme/core/config";
import { licenseKey } from "./devextreme-license";
import MemberProfilePublic from "./Components/Member/MemberProfilePublic";
import UserProfilePublic from "./Components/User/UserProfilePublic";
import AdminFormsListPage from "./Components/Forms/AdminFormsListPage";
import FormBuilderPage from "./Components/Forms/FormBuilderPage";
import FormFillerRouteWrapper from "./Components/Forms/FormFillerRouteWrapper";
import AdminSubmissionList from "./Components/Forms/FormSubmissionAdmin";
import FormSubmissionsPage from "./Components/Forms/FormSubmissionPage";
import NewsletterEditorPage from "./Components/Newsletters/NewsletterEditorPage";
import AdminNewsletterListPage from "./Components/Newsletters/AdminNewsletterListPage";
import InternalAttendeeList from "./Components/Events/InternalGroupedList";
import EventSponsorsPage from "./Components/Events/EventSponsorsPage";
import ConferenceSchedulePage from "./Components/Events/ConferenceSchedulePage";
import MeetingInvitePage from "./Components/Meetings/MeetingInvitePage";
import MeetingAcceptPage from "./Components/Meetings/MeetingAcceptPage";
import SchedulePage from "./Components/Meetings/SchedulePage";
import PublicAttendeeList from "./Components/Events/PublicList";
import ContactSessionMapPage from "./Components/Events/ContactSessionMapPage";
import ThankYouPage from "./Components/Forms/ThankYou";
import FEMAAppInfo from "./Components/User/FEMAAppInfo";
import AdminPagesPage from "./Components/Admin/AdminPagesPage";
import EditMyMemberPage from "./Components/Member/EditMyMemberPage";
import AdminFilesPage from "./Components/Admin/AdminFilesPage";
import AdminAdsPage from "./Components/Admin/AdminAdsPage";
import AdminEventsPage from "./Components/Admin/AdminEventsPage";
import AdminFormsPage from "./Components/Admin/AdminFormsPage";
import AdminNewslettersPage from "./Components/Admin/AdminNewslettersPage";
import AdminInactiveMembersPage from "./Components/Admin/AdminInactiveMembersPage";
import AdminMemberEngagementPage from "./Components/Admin/AdminMemberEngagementPage";
import AdminEventSponsorsPage from "./Components/Admin/AdminEventSponsorsPage";
import PartnerOpportunitiesPage from "./Components/PartnerOpportunities/PartnerOpportunitiesPage";
import PartnerOpportunityRegionsAdmin from "./Components/PartnerOpportunities/PartnerOpportunityRegionsAdmin";
import AwardsVotingPage from "./Components/Awards/AwardsVotingPage";
import AdminAwardsVotingPage from "./Components/Awards/AdminAwardsVotingPage";
import AdminAwardsEditPage from "./Components/Awards/AdminAwardsEditPage";
import AdminEmailsSentPage from "./Components/Admin/AdminEmailsSentPage";
import AdminPushNotificationsPage from "./Components/Admin/AdminPushNotificationsPage";
import AdminQuickBooksPage from "./Components/Admin/AdminQuickBooksPage";

config({ licenseKey });

const router = createBrowserRouter([
  {
    element: <AppPublic />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <Login />,
        path: "/Login",
      },
      {
        element: <ForgotPassword />,
        path: "/forgotpassword",
        errorElement: <ErrorPage />,
      },
      {
        element: <ResetPassword />,
        path: "/resetpassword/:guid",
        errorElement: <ErrorPage />,
      },
      {
        path: "/memberapplication/apply",
        element: <MemberApplication />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/memberapplication/newapplicant/:subscriptionId",
        element: <NewApplicant />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/memberapplication/thankyou",
        element: <ThankYou />,
      },
      {
        path: "/member/:name/:id",
        element: <MemberProfilePublic />,
      },
      {
        path: "/user/:name/:id",
        element: <UserProfilePublic />,
      },
      {
        path: "/submitform/:id",
        element: <FormFillerRouteWrapper />,
      },
      {
        path: "/events/:id/attending",
        element: <PublicAttendeeList />,
      },
      {
        path: "/submitform/thank-you",
        element: <ThankYouPage />,
      },
      {
        path: "/meetings/accept/:token",
        element: <MeetingAcceptPage />,
      },
      {
        path: "/industryshowcase",
        element: <IndustryShowcasePage isPublic />,
      },
      {
        path: "/events/:id",
        element: <ContactSessionMapPage />,
      },
    ],
  },
  {
    element: (
      <RequireAuth>
        <App />
      </RequireAuth>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        element: <Dashboard />,
      },
      {
        path: "/members",
        element: <MemberList />,
      },
      {
        path: "/members/tags",
        element: <MemberTags />,
      },
      {
        path: "/members/directory",
        element: <MemberDirectory />,
      },

      {
        path: "/member/files",
        element: <DownloadFiles />,
      },
      {
        path: "/members/resources",
        element: <MemberResources />,
      },
      {
        path: "/members/fallconvention",
        element: <FallConvention />,
      },
      {
        path: "/members/springmeeting",
        element: <SpringMeeting />,
      },
      {
        path: "/members/industryshowcase",
        element: <IndustryShowcase />,
      },
      {
        path: "/member/:id",
        element: <MemberProfile />,
      },
      {
        path: "/edit/member/:id",
        element: <EditMemberProfile />,
      },
      {
        path: "/users",
        element: <UserList />,
      },
      {
        path: "/users/fema",
        element: <FEMAAppInfo />,
      },
      {
        path: "/user/:id",
        element: <UserProfile />,
      },
      {
        path: "/edit/user/:id",
        element: <EditUserProfile />,
      },
      {
        path: "/member/invoices",
        element: <InvoiceList />,
      },
      {
        path: "/page/:id",
        element: <CustomSitePage />,
      },
      {
        path: "/member/my-page",
        element: <EditMyMemberPage />,
      },
      {
        path: "/forms/manage",
        element: <AdminFormsListPage />,
      },
      {
        path: "/forms/manage/:id",
        element: <FormBuilderPage />,
      },
      {
        path: "/forms/new",
        element: <FormBuilderPage />,
      },
      {
        path: "/forms/thank-you",
        element: <ThankYouPage />,
      },
      {
        path: "/forms/:id/responses",
        element: <FormSubmissionsPage />,
      },
      {
        path: "/forms/fill/:id",
        element: <FormFillerRouteWrapper />,
      },
      {
        path: "/forms/admin/submissions",
        element: <AdminSubmissionList />,
      },
      {
        path: "/newsletters/edit/:id?",
        element: <NewsletterEditorPage />,
      },
      {
        path: "/newsletters/new",
        element: <NewsletterEditorPage />,
      },
      {
        path: "/newsletters",
        element: <AdminNewsletterListPage />,
      },
      {
        path: "/events/:id?/participants",
        element: <InternalAttendeeList />,
      },
      {
        path: "/events/:id/sponsors",
        element: <EventSponsorsPage />,
      },
      {
        path: "/events/:id/schedule",
        element: <ConferenceSchedulePage />,
      },
      {
        path: "/schedule",
        element: <SchedulePage />,
      },
      {
        path: "/meetings/invite/:requestId",
        element: <MeetingInvitePage />,
      },
      {
        path: "/admin/pages",
        element: <AdminPagesPage />,
      },
      {
        path: "/admin/files",
        element: <AdminFilesPage />,
      },
      {
        path: "/admin/ads",
        element: <AdminAdsPage />,
      },
      {
        path: "/admin/events",
        element: <AdminEventsPage />,
      },
      {
        path: "/admin/forms",
        element: <AdminFormsPage />,
      },
      {
        path: "/admin/newsletters",
        element: <AdminNewslettersPage />,
      },
      {
        path: "/admin/inactive-members",
        element: <AdminInactiveMembersPage />,
      },
      {
        path: "/admin/member-engagement",
        element: <AdminMemberEngagementPage />,
      },
      {
        path: "/admin/sponsors",
        element: <AdminEventSponsorsPage />,
      },
      {
        path: "/partner-opportunities",
        element: <PartnerOpportunitiesPage />,
      },
      {
        path: "/admin/partner-opportunities",
        element: <PartnerOpportunitiesPage />,
      },
      {
        path: "/admin/partner-opportunities/regions",
        element: <PartnerOpportunityRegionsAdmin />,
      },
      {
        path: "/members/awards/vote/:periodId",
        element: <AwardsVotingPage />,
      },
      {
        path: "/members/awards/vote",
        element: <AwardsVotingPage />,
      },
      {
        path: "/admin/awards/new",
        element: <AdminAwardsEditPage />,
      },
      {
        path: "/admin/awards/:periodId",
        element: <AdminAwardsEditPage />,
      },
      {
        path: "/admin/awards",
        element: <AdminAwardsVotingPage />,
      },
      {
        path: "/admin/emails",
        element: <AdminEmailsSentPage />,
      },
      {
        path: "/admin/push-notifications",
        element: <AdminPushNotificationsPage />,
      },
      {
        path: "/admin/quickbooks",
        element: <AdminQuickBooksPage />,
      },
    ],
  },
]);

const token = localStorage.getItem("EMDAToken");
if (token) {
  const decoded: DecodedToken = jwtDecode(token);
  const now = new Date();
  store.dispatch(
    setUserFromToken({
      email:
        decoded[
          "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
        ],
      exp: decoded.exp,
    })
  );
  const isValid = isAfter(toDate(decoded.exp * 1000), now);
  if (isValid) {
    store.dispatch(fetchCurrentUser());
  }
}

createRoot(document.getElementById("root")!).render(
  <Provider store={store}>
    <RouterProvider router={router} />
    <ToastContainer
      position="bottom-center"
      autoClose={4000}
      limit={3}
      closeOnClick
      theme="dark"
    />
  </Provider>
);
